import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import ImageUpload from '../../components/ImageUpload';
import { formatNumberWithCommas, parseNumberFromFormatted } from '../../utils/helper';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [pictureFile, setPictureFile] = useState(null);
  const [pictureUrl, setPictureUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stockUnit, setStockUnit] = useState('piece');

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    price: '',
    stock: '',
    alert_quantity: 5,
    description: '',
    status: true,
    product_category_id: '',
    lens_sub_category: '',
    lens_availability: '',
    lens_material: '',
    lens_product_type: '',
    lens_coating: '',
    lens_index_range: '',
    lens_extra_coating: '',
  });

  // Derived state to check if selected category is Lens
  const isLensCategory = () => {
      const selectedCat = categories.find(c => c.id == formData.product_category_id);
      return selectedCat && selectedCat.name.toLowerCase() === 'lens';
  };

  useEffect(() => {
    fetchCategories();
    if (id) {
      getSingleData();
    }
  }, [id]);

  const fetchCategories = async () => {
      try {
          const response = await ApiService.request({
              method: 'GET',
              url: 'admin/product-categories?per_page=100' // Get all categories
          });
          if (response.data.status) {
              setCategories(response.data.data.categories);
          }
      } catch (error) {
          console.error('Failed to fetch categories', error);
      }
  };

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/products/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const productData = data.data; 
        setFormData({
          name: productData.name || '',
          sku: productData.sku || '',
          price: productData.price || '',
          stock: productData.stock || '',
          alert_quantity: productData.alert_quantity || 5,
          description: productData.description || '',
          status: productData.status !== undefined ? productData.status : true,
          product_category_id: productData.product_category_id || '',
          lens_sub_category: productData.lens_sub_category || '',
          lens_availability: productData.lens_availability || '',
          lens_material: productData.lens_material || '',
          lens_product_type: productData.lens_product_type || '',
          lens_coating: productData.lens_coating || '',
          lens_index_range: productData.lens_index_range || '',
          lens_extra_coating: productData.lens_extra_coating || '',
        });
        setPictureUrl(productData.image || null);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load product data');
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    if (name === 'price') {
        newValue = parseNumberFromFormatted(newValue);
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: newValue,
    }));
  };

  const handleImageChange = (name, file) => {
    if (name === 'image') {
      setPictureFile(file);
      if (file) {
        setPictureUrl(URL.createObjectURL(file));
      } else {
        setPictureUrl(null);
      }
    }
  };

  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    const currentStock = parseFloat(formData.stock) || 0;
    
    let newStock = currentStock;
    if (stockUnit === 'piece' && newUnit === 'pair') {
        newStock = currentStock / 2;
    } else if (stockUnit === 'pair' && newUnit === 'piece') {
        newStock = currentStock * 2;
    }
    
    setStockUnit(newUnit);
    setFormData(prev => ({ ...prev, stock: newStock }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoader(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === 'stock') {
             let stockVal = formData[key];
             if (stockUnit === 'pair') {
                 stockVal = stockVal * 2;
             }
             // Ensure integer for stock
             stockVal = Math.round(stockVal);
             data.append(key, stockVal);
        } else {
             data.append(key, formData[key]);
        }
      });

      if (pictureFile) {
        data.append('image', pictureFile);
      }

      if (id) {
        data.append('_method', 'PUT');
      }

      const response = await ApiService.request({
        method: 'POST', 
        url: id ? `admin/products/${id}` : 'admin/products',
        data: data,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/products');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Product' : 'Add Product'}
            </h1>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            <Link to="/products" className="btn btn-sm fw-bold btn-secondary">
              Back
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-fluid">
          <div className="card">
            <div className="card-body py-4">
              {isLoader && !id ? (
                <Loader />
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-12 mb-3 text-center">
                       <ImageUpload
                            name="image"
                            label="Product Image"
                            imageUrl={pictureUrl}
                            onImageChange={handleImageChange}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control form-control-solid"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Category</label>
                      <select
                        name="product_category_id"
                        className="form-select"
                        value={formData.product_category_id}
                        onChange={handleChange}
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isLensCategory() && (
                        <>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Sub Category</label>
                                <select
                                    name="lens_sub_category"
                                    className="form-select"
                                    value={formData.lens_sub_category}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Sub Category</option>
                                    <option value="Single Vision">Single Vision</option>
                                    <option value="Bifocals">Bifocals</option>
                                    <option value="Progressive">Progressive</option>
                                </select>
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label">Availability</label>
                                <select
                                    name="lens_availability"
                                    className="form-select"
                                    value={formData.lens_availability}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Availability</option>
                                    <option value="Stock">Stock</option>
                                    <option value="Order">Order</option>
                                </select>
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label">Material</label>
                                <select
                                    name="lens_material"
                                    className="form-select"
                                    value={formData.lens_material}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Material</option>
                                    <option value="CR39">CR39</option>
                                    <option value="Polycarbonate">Polycarbonate</option>
                                </select>
                            </div>

                            {formData.lens_sub_category === 'Single Vision' && (
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Product Type</label>
                                    <select
                                        name="lens_product_type"
                                        className="form-select"
                                        value={formData.lens_product_type}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Product Type</option>
                                        <option value="Near">Near</option>
                                        <option value="Distance">Distance</option>
                                    </select>
                                </div>
                            )}

                            <div className="col-md-6 mb-3">
                                <label className="form-label">Coating</label>
                                <select
                                    name="lens_coating"
                                    className="form-select"
                                    value={formData.lens_coating}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Coating</option>
                                    <option value="HMC">HMC</option>
                                    <option value="Transition">Transition</option>
                                    <option value="Bluecut">Bluecut</option>
                                    <option value="HMC Bluecut">HMC Bluecut</option>
                                    <option value="HMC Transition">HMC Transition</option>
                                    <option value="Bluecut Transition">Bluecut Transition</option>
                                </select>
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label">High Index Range</label>
                                <input
                                    type="text"
                                    name="lens_index_range"
                                    className="form-control form-control-solid"
                                    value={formData.lens_index_range}
                                    onChange={handleChange}
                                    placeholder="Enter Index Range"
                                />
                            </div>

                            {formData.lens_availability === 'Order' && (
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Extra Coating</label>
                                    <select
                                        name="lens_extra_coating"
                                        className="form-control form-control-solid"
                                        value={formData.lens_extra_coating}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Extra Coating</option>
                                        <option value="Anti-Fog">Anti-Fog</option>
                                        <option value="Anti-Reflective">Anti-Reflective</option>
                                        <option value="Scratch Resistant">Scratch Resistant</option>
                                        <option value="UV Protection">UV Protection</option>
                                        <option value="Mirror Coating">Mirror Coating</option>
                                        <option value="Tinted">Tinted</option>
                                    </select>
                                </div>
                            )}
                        </>
                    )}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">SKU</label>
                      <input
                        type="text"
                        name="sku"
                        className="form-control form-control-solid"
                        value={formData.sku}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Price</label>
                      <input
                        type="text"
                        name="price"
                        className="form-control form-control-solid"
                        value={formatNumberWithCommas(formData.price)}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Stock</label>
                      <div className="input-group">
                        <input
                          type="number"
                          name="stock"
                          className="form-control form-control-solid"
                          value={formData.stock}
                          onChange={handleChange}
                          required
                          step="any"
                        />
                        {isLensCategory() ? (
                            <select 
                                className="form-select form-select-solid" 
                                style={{maxWidth: '120px'}}
                                value={stockUnit}
                                onChange={handleUnitChange}
                            >
                                <option value="piece">Piece</option>
                                <option value="pair">Pair</option>
                            </select>
                        ) : (
                            <span className="input-group-text">Piece</span>
                        )}
                      </div>
                      {isLensCategory() && (
                          <div className="form-text text-muted">
                             Equivalent to: {stockUnit === 'piece' ? `${(parseFloat(formData.stock) || 0) / 2} Pairs` : `${(parseFloat(formData.stock) || 0) * 2} Pieces`}
                          </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Low Stock Alert Quantity</label>
                      <input
                        type="number"
                        name="alert_quantity"
                        className="form-control form-control-solid"
                        value={formData.alert_quantity}
                        onChange={handleChange}
                        required
                        min="0"
                      />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        className="form-control form-control-solid"
                        rows="3"
                        value={formData.description}
                        onChange={handleChange}
                      ></textarea>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch form-check-custom form-check-solid">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="status"
                          id="status"
                          checked={formData.status}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="status">
                          Active Status
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="text-end">
                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                      {isLoader ? 'Saving...' : 'Save Product'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
